import type { BrandingCssVariables } from "@/api/admin/branding"

import { DEFAULT_CSS_VAR_NAMES } from "./branding-config-constants"

const HSL_TRIPLET_RE = /^\d{1,3}(?:\.\d+)?\s\d{1,3}(?:\.\d+)?%\s\d{1,3}(?:\.\d+)?%$/
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/** Normalize API css_variables (`{}` or partial themes) to `{ light, dark }` for dirty checks. */
export function cloneCss(css?: BrandingCssVariables): BrandingCssVariables {
  return {
    light: { ...(css?.light ?? {}) },
    dark: { ...(css?.dark ?? {}) },
  }
}

function withoutBlankValues(theme?: Record<string, string>): Record<string, string> {
  const next: Record<string, string> = {}
  for (const [name, value] of Object.entries(theme ?? {})) {
    if (value.trim()) {
      next[name] = value
    }
  }
  return next
}

/** Drop blank/whitespace token keys so save can revert a token to build-time (backend rejects ""). */
export function omitBlankCssValues(css?: BrandingCssVariables): BrandingCssVariables {
  return {
    light: withoutBlankValues(css?.light),
    dark: withoutBlankValues(css?.dark),
  }
}

export function isHslTriplet(value: string): boolean {
  return HSL_TRIPLET_RE.test(value.trim())
}

export function hexToHslTriplet(hex: string): string | null {
  const raw = hex.trim()
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(raw)
  if (!match) {
    return null
  }
  let digits = match[1]
  if (digits.length === 3) {
    digits = digits
      .split("")
      .map((char) => char + char)
      .join("")
  }
  const r = parseInt(digits.slice(0, 2), 16) / 255
  const g = parseInt(digits.slice(2, 4), 16) / 255
  const b = parseInt(digits.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const lightness = (max + min) / 2
  let saturation = 0
  let hue = 0
  if (max !== min) {
    const delta = max - min
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
    if (max === r) {
      hue = (g - b) / delta + (g < b ? 6 : 0)
    } else if (max === g) {
      hue = (b - r) / delta + 2
    } else {
      hue = (r - g) / delta + 4
    }
    hue *= 60
  }
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`
}

export function hslTripletToHex(triplet: string): string | null {
  const match = /^(\d{1,3}(?:\.\d+)?)\s(\d{1,3}(?:\.\d+)?)%\s(\d{1,3}(?:\.\d+)?)%$/.exec(
    triplet.trim()
  )
  if (!match) {
    return null
  }
  const h = Number(match[1]) / 360
  const s = Number(match[2]) / 100
  const l = Number(match[3]) / 100
  if (![h, s, l].every((part) => Number.isFinite(part))) {
    return null
  }
  const hueToRgb = (p: number, q: number, t: number) => {
    let tone = t
    if (tone < 0) tone += 1
    if (tone > 1) tone -= 1
    if (tone < 1 / 6) return p + (q - p) * 6 * tone
    if (tone < 1 / 2) return q
    if (tone < 2 / 3) return p + (q - p) * (2 / 3 - tone) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = Math.round(hueToRgb(p, q, h + 1 / 3) * 255)
  const g = Math.round(hueToRgb(p, q, h) * 255)
  const b = Math.round(hueToRgb(p, q, h - 1 / 3) * 255)
  const toHex = (channel: number) => channel.toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export function pickerValueForCss(value: string | undefined): string {
  if (!value) {
    return "#000000"
  }
  if (HEX_RE.test(value)) {
    return value.length === 4
      ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
      : value
  }
  return hslTripletToHex(value) ?? "#000000"
}

/** Every token the editor offers: the full default palette plus any stored extra. */
export function cssVarNames(css: BrandingCssVariables): string[] {
  const names = new Set<string>(DEFAULT_CSS_VAR_NAMES)
  for (const theme of ["light", "dark"] as const) {
    for (const key of Object.keys(css[theme] ?? {})) {
      names.add(key)
    }
  }
  return [...names]
}

/** What prompts will call the assistant: its own name, else the app name, else the default. */
export function resolvedAssistantName(assistantName: string, appName: string): string {
  const assistant = assistantName.trim()
  if (assistant) {
    return assistant
  }
  const app = appName.trim()
  if (app) {
    return app
  }
  return "Hello AI"
}
